/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const _t = globalThis, jt = _t.ShadowRoot && (_t.ShadyCSS === void 0 || _t.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, we = Symbol(), Zt = /* @__PURE__ */ new WeakMap();
let He = class {
  constructor(t, e, n) {
    if (this._$cssResult$ = !0, n !== we) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (jt && t === void 0) {
      const n = e !== void 0 && e.length === 1;
      n && (t = Zt.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), n && Zt.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const ge = (i) => new He(typeof i == "string" ? i : i + "", void 0, we), Me = (i, t) => {
  if (jt) i.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const n = document.createElement("style"), o = _t.litNonce;
    o !== void 0 && n.setAttribute("nonce", o), n.textContent = e.cssText, i.appendChild(n);
  }
}, Qt = jt ? (i) => i : (i) => i instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const n of t.cssRules) e += n.cssText;
  return ge(e);
})(i) : i;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ue, defineProperty: De, getOwnPropertyDescriptor: Ne, getOwnPropertyNames: Ie, getOwnPropertySymbols: ze, getPrototypeOf: Be } = Object, z = globalThis, te = z.trustedTypes, je = te ? te.emptyScript : "", Ht = z.reactiveElementPolyfillSupport, dt = (i, t) => i, Et = { toAttribute(i, t) {
  switch (t) {
    case Boolean:
      i = i ? je : null;
      break;
    case Object:
    case Array:
      i = i == null ? i : JSON.stringify(i);
  }
  return i;
}, fromAttribute(i, t) {
  let e = i;
  switch (t) {
    case Boolean:
      e = i !== null;
      break;
    case Number:
      e = i === null ? null : Number(i);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(i);
      } catch {
        e = null;
      }
  }
  return e;
} }, Ft = (i, t) => !Ue(i, t), ee = { attribute: !0, type: String, converter: Et, reflect: !1, useDefault: !1, hasChanged: Ft };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), z.litPropertyMetadata ?? (z.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let Q = class extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ?? (this.l = [])).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = ee) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((e = Object.create(e)).wrapped = !0), this.elementProperties.set(t, e), !e.noAccessor) {
      const n = Symbol(), o = this.getPropertyDescriptor(t, n, e);
      o !== void 0 && De(this.prototype, t, o);
    }
  }
  static getPropertyDescriptor(t, e, n) {
    const { get: o, set: s } = Ne(this.prototype, t) ?? { get() {
      return this[e];
    }, set(r) {
      this[e] = r;
    } };
    return { get: o, set(r) {
      const a = o == null ? void 0 : o.call(this);
      s == null || s.call(this, r), this.requestUpdate(t, a, n);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? ee;
  }
  static _$Ei() {
    if (this.hasOwnProperty(dt("elementProperties"))) return;
    const t = Be(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(dt("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(dt("properties"))) {
      const e = this.properties, n = [...Ie(e), ...ze(e)];
      for (const o of n) this.createProperty(o, e[o]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [n, o] of e) this.elementProperties.set(n, o);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, n] of this.elementProperties) {
      const o = this._$Eu(e, n);
      o !== void 0 && this._$Eh.set(o, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const n = new Set(t.flat(1 / 0).reverse());
      for (const o of n) e.unshift(Qt(o));
    } else t !== void 0 && e.push(Qt(t));
    return e;
  }
  static _$Eu(t, e) {
    const n = e.attribute;
    return n === !1 ? void 0 : typeof n == "string" ? n : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var t;
    this._$ES = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (t = this.constructor.l) == null || t.forEach((e) => e(this));
  }
  addController(t) {
    var e;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(t), this.renderRoot !== void 0 && this.isConnected && ((e = t.hostConnected) == null || e.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$EO) == null || e.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const n of e.keys()) this.hasOwnProperty(n) && (t.set(n, this[n]), delete this[n]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Me(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var t;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$EO) == null || t.forEach((e) => {
      var n;
      return (n = e.hostConnected) == null ? void 0 : n.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$EO) == null || t.forEach((e) => {
      var n;
      return (n = e.hostDisconnected) == null ? void 0 : n.call(e);
    });
  }
  attributeChangedCallback(t, e, n) {
    this._$AK(t, n);
  }
  _$ET(t, e) {
    var s;
    const n = this.constructor.elementProperties.get(t), o = this.constructor._$Eu(t, n);
    if (o !== void 0 && n.reflect === !0) {
      const r = (((s = n.converter) == null ? void 0 : s.toAttribute) !== void 0 ? n.converter : Et).toAttribute(e, n.type);
      this._$Em = t, r == null ? this.removeAttribute(o) : this.setAttribute(o, r), this._$Em = null;
    }
  }
  _$AK(t, e) {
    var s, r;
    const n = this.constructor, o = n._$Eh.get(t);
    if (o !== void 0 && this._$Em !== o) {
      const a = n.getPropertyOptions(o), l = typeof a.converter == "function" ? { fromAttribute: a.converter } : ((s = a.converter) == null ? void 0 : s.fromAttribute) !== void 0 ? a.converter : Et;
      this._$Em = o, this[o] = l.fromAttribute(e, a.type) ?? ((r = this._$Ej) == null ? void 0 : r.get(o)) ?? null, this._$Em = null;
    }
  }
  requestUpdate(t, e, n) {
    var o;
    if (t !== void 0) {
      const s = this.constructor, r = this[t];
      if (n ?? (n = s.getPropertyOptions(t)), !((n.hasChanged ?? Ft)(r, e) || n.useDefault && n.reflect && r === ((o = this._$Ej) == null ? void 0 : o.get(t)) && !this.hasAttribute(s._$Eu(t, n)))) return;
      this.C(t, e, n);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, e, { useDefault: n, reflect: o, wrapped: s }, r) {
    n && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(t) && (this._$Ej.set(t, r ?? e ?? this[t]), s !== !0 || r !== void 0) || (this._$AL.has(t) || (this.hasUpdated || n || (e = void 0), this._$AL.set(t, e)), o === !0 && this._$Em !== t && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var n;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [s, r] of this._$Ep) this[s] = r;
        this._$Ep = void 0;
      }
      const o = this.constructor.elementProperties;
      if (o.size > 0) for (const [s, r] of o) {
        const { wrapped: a } = r, l = this[s];
        a !== !0 || this._$AL.has(s) || l === void 0 || this.C(s, void 0, r, l);
      }
    }
    let t = !1;
    const e = this._$AL;
    try {
      t = this.shouldUpdate(e), t ? (this.willUpdate(e), (n = this._$EO) == null || n.forEach((o) => {
        var s;
        return (s = o.hostUpdate) == null ? void 0 : s.call(o);
      }), this.update(e)) : this._$EM();
    } catch (o) {
      throw t = !1, this._$EM(), o;
    }
    t && this._$AE(e);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$EO) == null || e.forEach((n) => {
      var o;
      return (o = n.hostUpdated) == null ? void 0 : o.call(n);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((e) => this._$ET(e, this[e]))), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
Q.elementStyles = [], Q.shadowRootOptions = { mode: "open" }, Q[dt("elementProperties")] = /* @__PURE__ */ new Map(), Q[dt("finalized")] = /* @__PURE__ */ new Map(), Ht == null || Ht({ ReactiveElement: Q }), (z.reactiveElementVersions ?? (z.reactiveElementVersions = [])).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ft = globalThis, St = ft.trustedTypes, ie = St ? St.createPolicy("lit-html", { createHTML: (i) => i }) : void 0, ye = "$lit$", N = `lit$${Math.random().toFixed(9).slice(2)}$`, ve = "?" + N, Fe = `<${ve}>`, K = document, ut = () => K.createComment(""), mt = (i) => i === null || typeof i != "object" && typeof i != "function", Vt = Array.isArray, Ve = (i) => Vt(i) || typeof (i == null ? void 0 : i[Symbol.iterator]) == "function", Mt = `[ 	
\f\r]`, ht = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ne = /-->/g, oe = />/g, W = RegExp(`>|${Mt}(?:([^\\s"'>=/]+)(${Mt}*=${Mt}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), re = /'/g, se = /"/g, xe = /^(?:script|style|textarea|title)$/i, We = (i) => (t, ...e) => ({ _$litType$: i, strings: t, values: e }), qe = We(1), et = Symbol.for("lit-noChange"), b = Symbol.for("lit-nothing"), ae = /* @__PURE__ */ new WeakMap(), q = K.createTreeWalker(K, 129);
function be(i, t) {
  if (!Vt(i) || !i.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return ie !== void 0 ? ie.createHTML(t) : t;
}
const Ye = (i, t) => {
  const e = i.length - 1, n = [];
  let o, s = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", r = ht;
  for (let a = 0; a < e; a++) {
    const l = i[a];
    let c, f, h = -1, p = 0;
    for (; p < l.length && (r.lastIndex = p, f = r.exec(l), f !== null); ) p = r.lastIndex, r === ht ? f[1] === "!--" ? r = ne : f[1] !== void 0 ? r = oe : f[2] !== void 0 ? (xe.test(f[2]) && (o = RegExp("</" + f[2], "g")), r = W) : f[3] !== void 0 && (r = W) : r === W ? f[0] === ">" ? (r = o ?? ht, h = -1) : f[1] === void 0 ? h = -2 : (h = r.lastIndex - f[2].length, c = f[1], r = f[3] === void 0 ? W : f[3] === '"' ? se : re) : r === se || r === re ? r = W : r === ne || r === oe ? r = ht : (r = W, o = void 0);
    const d = r === W && i[a + 1].startsWith("/>") ? " " : "";
    s += r === ht ? l + Fe : h >= 0 ? (n.push(c), l.slice(0, h) + ye + l.slice(h) + N + d) : l + N + (h === -2 ? a : d);
  }
  return [be(i, s + (i[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), n];
};
class wt {
  constructor({ strings: t, _$litType$: e }, n) {
    let o;
    this.parts = [];
    let s = 0, r = 0;
    const a = t.length - 1, l = this.parts, [c, f] = Ye(t, e);
    if (this.el = wt.createElement(c, n), q.currentNode = this.el.content, e === 2 || e === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (o = q.nextNode()) !== null && l.length < a; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) for (const h of o.getAttributeNames()) if (h.endsWith(ye)) {
          const p = f[r++], d = o.getAttribute(h).split(N), u = /([.?@])?(.*)/.exec(p);
          l.push({ type: 1, index: s, name: u[2], strings: d, ctor: u[1] === "." ? Ke : u[1] === "?" ? Ge : u[1] === "@" ? Je : Tt }), o.removeAttribute(h);
        } else h.startsWith(N) && (l.push({ type: 6, index: s }), o.removeAttribute(h));
        if (xe.test(o.tagName)) {
          const h = o.textContent.split(N), p = h.length - 1;
          if (p > 0) {
            o.textContent = St ? St.emptyScript : "";
            for (let d = 0; d < p; d++) o.append(h[d], ut()), q.nextNode(), l.push({ type: 2, index: ++s });
            o.append(h[p], ut());
          }
        }
      } else if (o.nodeType === 8) if (o.data === ve) l.push({ type: 2, index: s });
      else {
        let h = -1;
        for (; (h = o.data.indexOf(N, h + 1)) !== -1; ) l.push({ type: 7, index: s }), h += N.length - 1;
      }
      s++;
    }
  }
  static createElement(t, e) {
    const n = K.createElement("template");
    return n.innerHTML = t, n;
  }
}
function it(i, t, e = i, n) {
  var r, a;
  if (t === et) return t;
  let o = n !== void 0 ? (r = e._$Co) == null ? void 0 : r[n] : e._$Cl;
  const s = mt(t) ? void 0 : t._$litDirective$;
  return (o == null ? void 0 : o.constructor) !== s && ((a = o == null ? void 0 : o._$AO) == null || a.call(o, !1), s === void 0 ? o = void 0 : (o = new s(i), o._$AT(i, e, n)), n !== void 0 ? (e._$Co ?? (e._$Co = []))[n] = o : e._$Cl = o), o !== void 0 && (t = it(i, o._$AS(i, t.values), o, n)), t;
}
class Xe {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: n } = this._$AD, o = ((t == null ? void 0 : t.creationScope) ?? K).importNode(e, !0);
    q.currentNode = o;
    let s = q.nextNode(), r = 0, a = 0, l = n[0];
    for (; l !== void 0; ) {
      if (r === l.index) {
        let c;
        l.type === 2 ? c = new yt(s, s.nextSibling, this, t) : l.type === 1 ? c = new l.ctor(s, l.name, l.strings, this, t) : l.type === 6 && (c = new Ze(s, this, t)), this._$AV.push(c), l = n[++a];
      }
      r !== (l == null ? void 0 : l.index) && (s = q.nextNode(), r++);
    }
    return q.currentNode = K, o;
  }
  p(t) {
    let e = 0;
    for (const n of this._$AV) n !== void 0 && (n.strings !== void 0 ? (n._$AI(t, n, e), e += n.strings.length - 2) : n._$AI(t[e])), e++;
  }
}
class yt {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this._$Cv;
  }
  constructor(t, e, n, o) {
    this.type = 2, this._$AH = b, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = n, this.options = o, this._$Cv = (o == null ? void 0 : o.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = it(this, t, e), mt(t) ? t === b || t == null || t === "" ? (this._$AH !== b && this._$AR(), this._$AH = b) : t !== this._$AH && t !== et && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : Ve(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== b && mt(this._$AH) ? this._$AA.nextSibling.data = t : this.T(K.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var s;
    const { values: e, _$litType$: n } = t, o = typeof n == "number" ? this._$AC(t) : (n.el === void 0 && (n.el = wt.createElement(be(n.h, n.h[0]), this.options)), n);
    if (((s = this._$AH) == null ? void 0 : s._$AD) === o) this._$AH.p(e);
    else {
      const r = new Xe(o, this), a = r.u(this.options);
      r.p(e), this.T(a), this._$AH = r;
    }
  }
  _$AC(t) {
    let e = ae.get(t.strings);
    return e === void 0 && ae.set(t.strings, e = new wt(t)), e;
  }
  k(t) {
    Vt(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let n, o = 0;
    for (const s of t) o === e.length ? e.push(n = new yt(this.O(ut()), this.O(ut()), this, this.options)) : n = e[o], n._$AI(s), o++;
    o < e.length && (this._$AR(n && n._$AB.nextSibling, o), e.length = o);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var n;
    for ((n = this._$AP) == null ? void 0 : n.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const o = t.nextSibling;
      t.remove(), t = o;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this._$Cv = t, (e = this._$AP) == null || e.call(this, t));
  }
}
class Tt {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, n, o, s) {
    this.type = 1, this._$AH = b, this._$AN = void 0, this.element = t, this.name = e, this._$AM = o, this.options = s, n.length > 2 || n[0] !== "" || n[1] !== "" ? (this._$AH = Array(n.length - 1).fill(new String()), this.strings = n) : this._$AH = b;
  }
  _$AI(t, e = this, n, o) {
    const s = this.strings;
    let r = !1;
    if (s === void 0) t = it(this, t, e, 0), r = !mt(t) || t !== this._$AH && t !== et, r && (this._$AH = t);
    else {
      const a = t;
      let l, c;
      for (t = s[0], l = 0; l < s.length - 1; l++) c = it(this, a[n + l], e, l), c === et && (c = this._$AH[l]), r || (r = !mt(c) || c !== this._$AH[l]), c === b ? t = b : t !== b && (t += (c ?? "") + s[l + 1]), this._$AH[l] = c;
    }
    r && !o && this.j(t);
  }
  j(t) {
    t === b ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Ke extends Tt {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === b ? void 0 : t;
  }
}
class Ge extends Tt {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== b);
  }
}
class Je extends Tt {
  constructor(t, e, n, o, s) {
    super(t, e, n, o, s), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = it(this, t, e, 0) ?? b) === et) return;
    const n = this._$AH, o = t === b && n !== b || t.capture !== n.capture || t.once !== n.once || t.passive !== n.passive, s = t !== b && (n === b || o);
    o && this.element.removeEventListener(this.name, this, n), s && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e;
    typeof this._$AH == "function" ? this._$AH.call(((e = this.options) == null ? void 0 : e.host) ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ze {
  constructor(t, e, n) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = n;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    it(this, t);
  }
}
const Ut = ft.litHtmlPolyfillSupport;
Ut == null || Ut(wt, yt), (ft.litHtmlVersions ?? (ft.litHtmlVersions = [])).push("3.3.0");
const Qe = (i, t, e) => {
  const n = (e == null ? void 0 : e.renderBefore) ?? t;
  let o = n._$litPart$;
  if (o === void 0) {
    const s = (e == null ? void 0 : e.renderBefore) ?? null;
    n._$litPart$ = o = new yt(t.insertBefore(ut(), s), s, void 0, e ?? {});
  }
  return o._$AI(i), o;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Y = globalThis;
class pt extends Q {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const t = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = t.firstChild), t;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Qe(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this._$Do) == null || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this._$Do) == null || t.setConnected(!1);
  }
  render() {
    return et;
  }
}
var me;
pt._$litElement$ = !0, pt.finalized = !0, (me = Y.litElementHydrateSupport) == null || me.call(Y, { LitElement: pt });
const Dt = Y.litElementPolyfillSupport;
Dt == null || Dt({ LitElement: pt });
(Y.litElementVersions ?? (Y.litElementVersions = [])).push("4.2.0");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ti = Symbol.for(""), ei = (i) => {
  if ((i == null ? void 0 : i.r) === ti) return i == null ? void 0 : i._$litStatic$;
}, le = /* @__PURE__ */ new Map(), ii = (i) => (t, ...e) => {
  const n = e.length;
  let o, s;
  const r = [], a = [];
  let l, c = 0, f = !1;
  for (; c < n; ) {
    for (l = t[c]; c < n && (s = e[c], (o = ei(s)) !== void 0); ) l += o + t[++c], f = !0;
    c !== n && a.push(s), r.push(l), c++;
  }
  if (c === n && r.push(t[n]), f) {
    const h = r.join("$$lit$$");
    (t = le.get(h)) === void 0 && (r.raw = r, le.set(h, t = r)), e = a;
  }
  return i(t, ...e);
}, $t = ii(qe);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ni = { attribute: !0, type: String, converter: Et, reflect: !1, hasChanged: Ft }, oi = (i = ni, t, e) => {
  const { kind: n, metadata: o } = e;
  let s = globalThis.litPropertyMetadata.get(o);
  if (s === void 0 && globalThis.litPropertyMetadata.set(o, s = /* @__PURE__ */ new Map()), n === "setter" && ((i = Object.create(i)).wrapped = !0), s.set(e.name, i), n === "accessor") {
    const { name: r } = e;
    return { set(a) {
      const l = t.get.call(this);
      t.set.call(this, a), this.requestUpdate(r, l, i);
    }, init(a) {
      return a !== void 0 && this.C(r, void 0, i, a), a;
    } };
  }
  if (n === "setter") {
    const { name: r } = e;
    return function(a) {
      const l = this[r];
      t.call(this, a), this.requestUpdate(r, l, i);
    };
  }
  throw Error("Unsupported decorator location: " + n);
};
function rt(i) {
  return (t, e) => typeof e == "object" ? oi(i, t, e) : ((n, o, s) => {
    const r = o.hasOwnProperty(s);
    return o.constructor.createProperty(s, n), r ? Object.getOwnPropertyDescriptor(o, s) : void 0;
  })(i, t, e);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ri = (i, t, e) => (e.configurable = !0, e.enumerable = !0, Reflect.decorate && typeof t != "object" && Object.defineProperty(i, t, e), e);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function si(i, t) {
  return (e, n, o) => {
    const s = (r) => {
      var a;
      return ((a = r.renderRoot) == null ? void 0 : a.querySelector(i)) ?? null;
    };
    return ri(e, n, { get() {
      return s(this);
    } });
  };
}
const ai = '/*! tailwindcss v4.1.11 | MIT License | https://tailwindcss.com */@layer properties{@supports (((-webkit-hyphens:none)) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-border-style:solid;--tw-shadow:0 0 #0000;--tw-shadow-color:initial;--tw-shadow-alpha:100%;--tw-inset-shadow:0 0 #0000;--tw-inset-shadow-color:initial;--tw-inset-shadow-alpha:100%;--tw-ring-color:initial;--tw-ring-shadow:0 0 #0000;--tw-inset-ring-color:initial;--tw-inset-ring-shadow:0 0 #0000;--tw-ring-inset:initial;--tw-ring-offset-width:0px;--tw-ring-offset-color:#fff;--tw-ring-offset-shadow:0 0 #0000;--tw-blur:initial;--tw-brightness:initial;--tw-contrast:initial;--tw-grayscale:initial;--tw-hue-rotate:initial;--tw-invert:initial;--tw-opacity:initial;--tw-saturate:initial;--tw-sepia:initial;--tw-drop-shadow:initial;--tw-drop-shadow-color:initial;--tw-drop-shadow-alpha:100%;--tw-drop-shadow-size:initial}}}:root,:host{--default-transition-duration:.15s;--default-transition-timing-function:cubic-bezier(.4,0,.2,1)}.visible{visibility:visible}.absolute{position:absolute}.fixed{position:fixed}.relative{position:relative}.static{position:static}.-top-8{top:calc(var(--bdk-space-8,5.333rem)*-1)}.isolate{isolation:isolate}.container{width:100%}@media (min-width:319px){.container{max-width:319px}}@media (min-width:320px){.container{max-width:320px}}@media (min-width:359px){.container{max-width:359px}}@media (min-width:360px){.container{max-width:360px}}@media (min-width:374px){.container{max-width:374px}}@media (min-width:375px){.container{max-width:375px}}@media (min-width:559px){.container{max-width:559px}}@media (min-width:560px){.container{max-width:560px}}@media (min-width:759px){.container{max-width:759px}}@media (min-width:760px){.container{max-width:760px}}@media (min-width:959px){.container{max-width:959px}}@media (min-width:960px){.container{max-width:960px}}@media (min-width:1159px){.container{max-width:1159px}}@media (min-width:1160px){.container{max-width:1160px}}@media (min-width:1359px){.container{max-width:1359px}}@media (min-width:1360px){.container{max-width:1360px}}@media (min-width:1659px){.container{max-width:1659px}}@media (min-width:1660px){.container{max-width:1660px}}@media (min-width:1919px){.container{max-width:1919px}}@media (min-width:1920px){.container{max-width:1920px}}@media (min-width:40rem){.container{max-width:40rem}}@media (min-width:48rem){.container{max-width:48rem}}@media (min-width:64rem){.container{max-width:64rem}}@media (min-width:80rem){.container{max-width:80rem}}@media (min-width:96rem){.container{max-width:96rem}}.m-4{margin:var(--bdk-space-4,2.667rem)}.mx-auto{margin-inline:auto}.mt-4{margin-top:var(--bdk-space-4,2.667rem)}.mt-6{margin-top:var(--bdk-space-6,4rem)}.mt-12{margin-top:var(--bdk-space-12,8rem)}.mb-8{margin-bottom:var(--bdk-space-8,5.333rem)}.block{display:block}.contents{display:contents}.flex{display:flex}.grid{display:grid}.hidden{display:none}.inline{display:inline}.table{display:table}.h-8{height:var(--bdk-space-8,5.333rem)}.h-10{height:var(--bdk-space-10,6.667rem)}.h-32{height:var(--bdk-space-32,21.333rem)}.h-full{height:100%}.w-8{width:var(--bdk-space-8,5.333rem)}.w-10{width:var(--bdk-space-10,6.667rem)}.w-32{width:var(--bdk-space-32,21.333rem)}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.resize{resize:both}.grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.flex-col{flex-direction:column}.flex-wrap{flex-wrap:wrap}.place-items-center{place-items:center}.items-center{align-items:center}.justify-center{justify-content:center}.gap-4{gap:var(--bdk-space-4,2.667rem)}.gap-8{gap:var(--bdk-space-8,5.333rem)}.border{border-style:var(--tw-border-style);border-width:1px}.border-2{border-style:var(--tw-border-style);border-width:2px}.border-dashed{--tw-border-style:dashed;border-style:dashed}.bg-pebble{background-color:var(--bdk-pebble,#e0dfde)}.pt-16{padding-top:var(--bdk-space-16,10.667rem)}.pb-24{padding-bottom:var(--bdk-space-24,16rem)}.text-center{text-align:center}.text-inherit{color:inherit}.lowercase{text-transform:lowercase}.shadow{--tw-shadow:0 1px 3px 0 var(--tw-shadow-color,#0000001a),0 1px 2px -1px var(--tw-shadow-color,#0000001a);box-shadow:var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)}.blur{--tw-blur:blur(8px);filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.filter{filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.transition{transition-property:color,background-color,border-color,outline-color,text-decoration-color,fill,stroke,--tw-gradient-from,--tw-gradient-via,--tw-gradient-to,opacity,box-shadow,transform,translate,scale,rotate,filter,-webkit-backdrop-filter,backdrop-filter,display,visibility,content-visibility,overlay,pointer-events;transition-timing-function:var(--tw-ease,var(--default-transition-timing-function));transition-duration:var(--tw-duration,var(--default-transition-duration))}.select-none{-webkit-user-select:none;user-select:none}@media (min-width:48rem){.md\\:flex-row{flex-direction:row}}:host(:where([popover])){margin:unset;inset:unset;overflow:unset;padding:unset;border:0}:host(:not(:popover-open)){display:none}:host{--sqrt2:1.4142;--bdk-tooltip-arrow-size:var(--bdk-space-6,24px);--bdk-tooltip-arrow-side:calc(var(--bdk-tooltip-arrow-size)/var(--sqrt2));--bdk-tooltip-max-width:min(100dvw,324px);max-width:var(--bdk-tooltip-max-width);text-align:start;background-color:var(--bdk-coal-100,#000);width:max-content;transition:opacity var(--bdk-transition-duration-default,.2s)ease,visibility var(--bdk-transition-duration-default,.2s)ease;color:var(--bdk-snow-100,#fff);border-radius:8px;display:inline-block;position:relative;overflow:visible}.tooltip--arrow{width:var(--bdk-tooltip-arrow-side);aspect-ratio:1;background-color:var(--bdk-coal-100);border-radius:2px;position:absolute;transform:rotate(45deg)}.tooltip--content{gap:var(--bdk-space-2,8px);padding:var(--bdk-space-4,16px);box-shadow:var(--bdk-shadow-elevation--1);word-wrap:break-word;display:grid}.tooltip--content:has(.tooltip--icon){grid-template-columns:auto 1fr}.tooltip--content:has(.tooltip--icon) .tooltip--icon{grid-column:1}.tooltip--content:has(.tooltip--icon) .tooltip--header,.tooltip--content:has(.tooltip--icon) .tooltip--text{grid-column:2}.tooltip--content:not(:has(.tooltip--icon)){grid-template-columns:1fr}.tooltip--content:not(:has(.tooltip--icon)) .tooltip--header,.tooltip--content:not(:has(.tooltip--icon)) .tooltip--text{grid-column:1}.tooltip--header{letter-spacing:.05rem;text-transform:uppercase;grid-row:1;align-self:center;font-family:Eduard Breuninger;font-size:2.667rem;font-weight:500;line-height:4rem;display:flex}.tooltip--text{letter-spacing:.05rem;text-transform:inherit;text-wrap:wrap;grid-area:1/2;align-self:center;font-family:Eduard Breuninger;font-size:2.333rem;font-weight:500;line-height:3.667rem}.tooltip--header+.tooltip--text{grid-row:2;align-self:self-start}.tooltip--icon{align-self:start;align-items:center;display:inline-flex}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-border-style{syntax:"*";inherits:false;initial-value:solid}@property --tw-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-shadow-color{syntax:"*";inherits:false}@property --tw-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-inset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-shadow-color{syntax:"*";inherits:false}@property --tw-inset-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-ring-color{syntax:"*";inherits:false}@property --tw-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-ring-color{syntax:"*";inherits:false}@property --tw-inset-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-ring-inset{syntax:"*";inherits:false}@property --tw-ring-offset-width{syntax:"<length>";inherits:false;initial-value:0}@property --tw-ring-offset-color{syntax:"*";inherits:false;initial-value:#fff}@property --tw-ring-offset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-blur{syntax:"*";inherits:false}@property --tw-brightness{syntax:"*";inherits:false}@property --tw-contrast{syntax:"*";inherits:false}@property --tw-grayscale{syntax:"*";inherits:false}@property --tw-hue-rotate{syntax:"*";inherits:false}@property --tw-invert{syntax:"*";inherits:false}@property --tw-opacity{syntax:"*";inherits:false}@property --tw-saturate{syntax:"*";inherits:false}@property --tw-sepia{syntax:"*";inherits:false}@property --tw-drop-shadow{syntax:"*";inherits:false}@property --tw-drop-shadow-color{syntax:"*";inherits:false}@property --tw-drop-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-drop-shadow-size{syntax:"*";inherits:false}', li = () => typeof crypto < "u" && crypto.randomUUID ? crypto.randomUUID() : "", ci = () => {
  let i = 0;
  const t = Date.now(), e = Math.floor(Math.random() * 1e3), n = `${t}-${e}`;
  return function* (o) {
    for (; ; )
      yield `${o}-${n}-${++i}`;
  };
}, hi = ci(), di = (i) => {
  const t = li();
  return t ? `${i}-${t}` : hi(i).next().value;
}, nt = Math.min, X = Math.max, Ct = Math.round, At = Math.floor, T = (i) => ({
  x: i,
  y: i
}), fi = {
  left: "right",
  right: "left",
  bottom: "top",
  top: "bottom"
}, pi = {
  start: "end",
  end: "start"
};
function It(i, t, e) {
  return X(i, nt(t, e));
}
function vt(i, t) {
  return typeof i == "function" ? i(t) : i;
}
function G(i) {
  return i.split("-")[0];
}
function xt(i) {
  return i.split("-")[1];
}
function $e(i) {
  return i === "x" ? "y" : "x";
}
function Wt(i) {
  return i === "y" ? "height" : "width";
}
const ui = /* @__PURE__ */ new Set(["top", "bottom"]);
function I(i) {
  return ui.has(G(i)) ? "y" : "x";
}
function qt(i) {
  return $e(I(i));
}
function mi(i, t, e) {
  e === void 0 && (e = !1);
  const n = xt(i), o = qt(i), s = Wt(o);
  let r = o === "x" ? n === (e ? "end" : "start") ? "right" : "left" : n === "start" ? "bottom" : "top";
  return t.reference[s] > t.floating[s] && (r = Ot(r)), [r, Ot(r)];
}
function wi(i) {
  const t = Ot(i);
  return [zt(i), t, zt(t)];
}
function zt(i) {
  return i.replace(/start|end/g, (t) => pi[t]);
}
const ce = ["left", "right"], he = ["right", "left"], gi = ["top", "bottom"], yi = ["bottom", "top"];
function vi(i, t, e) {
  switch (i) {
    case "top":
    case "bottom":
      return e ? t ? he : ce : t ? ce : he;
    case "left":
    case "right":
      return t ? gi : yi;
    default:
      return [];
  }
}
function xi(i, t, e, n) {
  const o = xt(i);
  let s = vi(G(i), e === "start", n);
  return o && (s = s.map((r) => r + "-" + o), t && (s = s.concat(s.map(zt)))), s;
}
function Ot(i) {
  return i.replace(/left|right|bottom|top/g, (t) => fi[t]);
}
function bi(i) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...i
  };
}
function Ae(i) {
  return typeof i != "number" ? bi(i) : {
    top: i,
    right: i,
    bottom: i,
    left: i
  };
}
function Pt(i) {
  const {
    x: t,
    y: e,
    width: n,
    height: o
  } = i;
  return {
    width: n,
    height: o,
    top: e,
    left: t,
    right: t + n,
    bottom: e + o,
    x: t,
    y: e
  };
}
function de(i, t, e) {
  let {
    reference: n,
    floating: o
  } = i;
  const s = I(t), r = qt(t), a = Wt(r), l = G(t), c = s === "y", f = n.x + n.width / 2 - o.width / 2, h = n.y + n.height / 2 - o.height / 2, p = n[a] / 2 - o[a] / 2;
  let d;
  switch (l) {
    case "top":
      d = {
        x: f,
        y: n.y - o.height
      };
      break;
    case "bottom":
      d = {
        x: f,
        y: n.y + n.height
      };
      break;
    case "right":
      d = {
        x: n.x + n.width,
        y: h
      };
      break;
    case "left":
      d = {
        x: n.x - o.width,
        y: h
      };
      break;
    default:
      d = {
        x: n.x,
        y: n.y
      };
  }
  switch (xt(t)) {
    case "start":
      d[r] -= p * (e && c ? -1 : 1);
      break;
    case "end":
      d[r] += p * (e && c ? -1 : 1);
      break;
  }
  return d;
}
const $i = async (i, t, e) => {
  const {
    placement: n = "bottom",
    strategy: o = "absolute",
    middleware: s = [],
    platform: r
  } = e, a = s.filter(Boolean), l = await (r.isRTL == null ? void 0 : r.isRTL(t));
  let c = await r.getElementRects({
    reference: i,
    floating: t,
    strategy: o
  }), {
    x: f,
    y: h
  } = de(c, n, l), p = n, d = {}, u = 0;
  for (let m = 0; m < a.length; m++) {
    const {
      name: g,
      fn: w
    } = a[m], {
      x: y,
      y: v,
      data: $,
      reset: x
    } = await w({
      x: f,
      y: h,
      initialPlacement: n,
      placement: p,
      strategy: o,
      middlewareData: d,
      rects: c,
      platform: r,
      elements: {
        reference: i,
        floating: t
      }
    });
    f = y ?? f, h = v ?? h, d = {
      ...d,
      [g]: {
        ...d[g],
        ...$
      }
    }, x && u <= 50 && (u++, typeof x == "object" && (x.placement && (p = x.placement), x.rects && (c = x.rects === !0 ? await r.getElementRects({
      reference: i,
      floating: t,
      strategy: o
    }) : x.rects), {
      x: f,
      y: h
    } = de(c, p, l)), m = -1);
  }
  return {
    x: f,
    y: h,
    placement: p,
    strategy: o,
    middlewareData: d
  };
};
async function _e(i, t) {
  var e;
  t === void 0 && (t = {});
  const {
    x: n,
    y: o,
    platform: s,
    rects: r,
    elements: a,
    strategy: l
  } = i, {
    boundary: c = "clippingAncestors",
    rootBoundary: f = "viewport",
    elementContext: h = "floating",
    altBoundary: p = !1,
    padding: d = 0
  } = vt(t, i), u = Ae(d), g = a[p ? h === "floating" ? "reference" : "floating" : h], w = Pt(await s.getClippingRect({
    element: (e = await (s.isElement == null ? void 0 : s.isElement(g))) == null || e ? g : g.contextElement || await (s.getDocumentElement == null ? void 0 : s.getDocumentElement(a.floating)),
    boundary: c,
    rootBoundary: f,
    strategy: l
  })), y = h === "floating" ? {
    x: n,
    y: o,
    width: r.floating.width,
    height: r.floating.height
  } : r.reference, v = await (s.getOffsetParent == null ? void 0 : s.getOffsetParent(a.floating)), $ = await (s.isElement == null ? void 0 : s.isElement(v)) ? await (s.getScale == null ? void 0 : s.getScale(v)) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  }, x = Pt(s.convertOffsetParentRelativeRectToViewportRelativeRect ? await s.convertOffsetParentRelativeRectToViewportRelativeRect({
    elements: a,
    rect: y,
    offsetParent: v,
    strategy: l
  }) : y);
  return {
    top: (w.top - x.top + u.top) / $.y,
    bottom: (x.bottom - w.bottom + u.bottom) / $.y,
    left: (w.left - x.left + u.left) / $.x,
    right: (x.right - w.right + u.right) / $.x
  };
}
const Ai = (i) => ({
  name: "arrow",
  options: i,
  async fn(t) {
    const {
      x: e,
      y: n,
      placement: o,
      rects: s,
      platform: r,
      elements: a,
      middlewareData: l
    } = t, {
      element: c,
      padding: f = 0
    } = vt(i, t) || {};
    if (c == null)
      return {};
    const h = Ae(f), p = {
      x: e,
      y: n
    }, d = qt(o), u = Wt(d), m = await r.getDimensions(c), g = d === "y", w = g ? "top" : "left", y = g ? "bottom" : "right", v = g ? "clientHeight" : "clientWidth", $ = s.reference[u] + s.reference[d] - p[d] - s.floating[u], x = p[d] - s.reference[d], L = await (r.getOffsetParent == null ? void 0 : r.getOffsetParent(c));
    let E = L ? L[v] : 0;
    (!E || !await (r.isElement == null ? void 0 : r.isElement(L))) && (E = a.floating[v] || s.floating[u]);
    const at = $ / 2 - x / 2, j = E / 2 - m[u] / 2 - 1, H = nt(h[w], j), lt = nt(h[y], j), F = H, ct = E - m[u] - lt, A = E / 2 - m[u] / 2 + at, V = It(F, A, ct), M = !l.arrow && xt(o) != null && A !== V && s.reference[u] / 2 - (A < F ? H : lt) - m[u] / 2 < 0, S = M ? A < F ? A - F : A - ct : 0;
    return {
      [d]: p[d] + S,
      data: {
        [d]: V,
        centerOffset: A - V - S,
        ...M && {
          alignmentOffset: S
        }
      },
      reset: M
    };
  }
}), _i = function(i) {
  return i === void 0 && (i = {}), {
    name: "flip",
    options: i,
    async fn(t) {
      var e, n;
      const {
        placement: o,
        middlewareData: s,
        rects: r,
        initialPlacement: a,
        platform: l,
        elements: c
      } = t, {
        mainAxis: f = !0,
        crossAxis: h = !0,
        fallbackPlacements: p,
        fallbackStrategy: d = "bestFit",
        fallbackAxisSideDirection: u = "none",
        flipAlignment: m = !0,
        ...g
      } = vt(i, t);
      if ((e = s.arrow) != null && e.alignmentOffset)
        return {};
      const w = G(o), y = I(a), v = G(a) === a, $ = await (l.isRTL == null ? void 0 : l.isRTL(c.floating)), x = p || (v || !m ? [Ot(a)] : wi(a)), L = u !== "none";
      !p && L && x.push(...xi(a, m, u, $));
      const E = [a, ...x], at = await _e(t, g), j = [];
      let H = ((n = s.flip) == null ? void 0 : n.overflows) || [];
      if (f && j.push(at[w]), h) {
        const A = mi(o, r, $);
        j.push(at[A[0]], at[A[1]]);
      }
      if (H = [...H, {
        placement: o,
        overflows: j
      }], !j.every((A) => A <= 0)) {
        var lt, F;
        const A = (((lt = s.flip) == null ? void 0 : lt.index) || 0) + 1, V = E[A];
        if (V && (!(h === "alignment" ? y !== I(V) : !1) || // We leave the current main axis only if every placement on that axis
        // overflows the main axis.
        H.every((C) => C.overflows[0] > 0 && I(C.placement) === y)))
          return {
            data: {
              index: A,
              overflows: H
            },
            reset: {
              placement: V
            }
          };
        let M = (F = H.filter((S) => S.overflows[0] <= 0).sort((S, C) => S.overflows[1] - C.overflows[1])[0]) == null ? void 0 : F.placement;
        if (!M)
          switch (d) {
            case "bestFit": {
              var ct;
              const S = (ct = H.filter((C) => {
                if (L) {
                  const D = I(C.placement);
                  return D === y || // Create a bias to the `y` side axis due to horizontal
                  // reading directions favoring greater width.
                  D === "y";
                }
                return !0;
              }).map((C) => [C.placement, C.overflows.filter((D) => D > 0).reduce((D, Le) => D + Le, 0)]).sort((C, D) => C[1] - D[1])[0]) == null ? void 0 : ct[0];
              S && (M = S);
              break;
            }
            case "initialPlacement":
              M = a;
              break;
          }
        if (o !== M)
          return {
            reset: {
              placement: M
            }
          };
      }
      return {};
    }
  };
}, Ei = /* @__PURE__ */ new Set(["left", "top"]);
async function Si(i, t) {
  const {
    placement: e,
    platform: n,
    elements: o
  } = i, s = await (n.isRTL == null ? void 0 : n.isRTL(o.floating)), r = G(e), a = xt(e), l = I(e) === "y", c = Ei.has(r) ? -1 : 1, f = s && l ? -1 : 1, h = vt(t, i);
  let {
    mainAxis: p,
    crossAxis: d,
    alignmentAxis: u
  } = typeof h == "number" ? {
    mainAxis: h,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: h.mainAxis || 0,
    crossAxis: h.crossAxis || 0,
    alignmentAxis: h.alignmentAxis
  };
  return a && typeof u == "number" && (d = a === "end" ? u * -1 : u), l ? {
    x: d * f,
    y: p * c
  } : {
    x: p * c,
    y: d * f
  };
}
const Ci = function(i) {
  return i === void 0 && (i = 0), {
    name: "offset",
    options: i,
    async fn(t) {
      var e, n;
      const {
        x: o,
        y: s,
        placement: r,
        middlewareData: a
      } = t, l = await Si(t, i);
      return r === ((e = a.offset) == null ? void 0 : e.placement) && (n = a.arrow) != null && n.alignmentOffset ? {} : {
        x: o + l.x,
        y: s + l.y,
        data: {
          ...l,
          placement: r
        }
      };
    }
  };
}, Oi = function(i) {
  return i === void 0 && (i = {}), {
    name: "shift",
    options: i,
    async fn(t) {
      const {
        x: e,
        y: n,
        placement: o
      } = t, {
        mainAxis: s = !0,
        crossAxis: r = !1,
        limiter: a = {
          fn: (g) => {
            let {
              x: w,
              y
            } = g;
            return {
              x: w,
              y
            };
          }
        },
        ...l
      } = vt(i, t), c = {
        x: e,
        y: n
      }, f = await _e(t, l), h = I(G(o)), p = $e(h);
      let d = c[p], u = c[h];
      if (s) {
        const g = p === "y" ? "top" : "left", w = p === "y" ? "bottom" : "right", y = d + f[g], v = d - f[w];
        d = It(y, d, v);
      }
      if (r) {
        const g = h === "y" ? "top" : "left", w = h === "y" ? "bottom" : "right", y = u + f[g], v = u - f[w];
        u = It(y, u, v);
      }
      const m = a.fn({
        ...t,
        [p]: d,
        [h]: u
      });
      return {
        ...m,
        data: {
          x: m.x - e,
          y: m.y - n,
          enabled: {
            [p]: s,
            [h]: r
          }
        }
      };
    }
  };
};
function kt() {
  return typeof window < "u";
}
function st(i) {
  return Ee(i) ? (i.nodeName || "").toLowerCase() : "#document";
}
function _(i) {
  var t;
  return (i == null || (t = i.ownerDocument) == null ? void 0 : t.defaultView) || window;
}
function R(i) {
  var t;
  return (t = (Ee(i) ? i.ownerDocument : i.document) || window.document) == null ? void 0 : t.documentElement;
}
function Ee(i) {
  return kt() ? i instanceof Node || i instanceof _(i).Node : !1;
}
function O(i) {
  return kt() ? i instanceof Element || i instanceof _(i).Element : !1;
}
function k(i) {
  return kt() ? i instanceof HTMLElement || i instanceof _(i).HTMLElement : !1;
}
function fe(i) {
  return !kt() || typeof ShadowRoot > "u" ? !1 : i instanceof ShadowRoot || i instanceof _(i).ShadowRoot;
}
const Pi = /* @__PURE__ */ new Set(["inline", "contents"]);
function bt(i) {
  const {
    overflow: t,
    overflowX: e,
    overflowY: n,
    display: o
  } = P(i);
  return /auto|scroll|overlay|hidden|clip/.test(t + n + e) && !Pi.has(o);
}
const Ti = /* @__PURE__ */ new Set(["table", "td", "th"]);
function ki(i) {
  return Ti.has(st(i));
}
const Ri = [":popover-open", ":modal"];
function Rt(i) {
  return Ri.some((t) => {
    try {
      return i.matches(t);
    } catch {
      return !1;
    }
  });
}
const Li = ["transform", "translate", "scale", "rotate", "perspective"], Hi = ["transform", "translate", "scale", "rotate", "perspective", "filter"], Mi = ["paint", "layout", "strict", "content"];
function Yt(i) {
  const t = Xt(), e = O(i) ? P(i) : i;
  return Li.some((n) => e[n] ? e[n] !== "none" : !1) || (e.containerType ? e.containerType !== "normal" : !1) || !t && (e.backdropFilter ? e.backdropFilter !== "none" : !1) || !t && (e.filter ? e.filter !== "none" : !1) || Hi.some((n) => (e.willChange || "").includes(n)) || Mi.some((n) => (e.contain || "").includes(n));
}
function Ui(i) {
  let t = B(i);
  for (; k(t) && !ot(t); ) {
    if (Yt(t))
      return t;
    if (Rt(t))
      return null;
    t = B(t);
  }
  return null;
}
function Xt() {
  return typeof CSS > "u" || !CSS.supports ? !1 : CSS.supports("-webkit-backdrop-filter", "none");
}
const Di = /* @__PURE__ */ new Set(["html", "body", "#document"]);
function ot(i) {
  return Di.has(st(i));
}
function P(i) {
  return _(i).getComputedStyle(i);
}
function Lt(i) {
  return O(i) ? {
    scrollLeft: i.scrollLeft,
    scrollTop: i.scrollTop
  } : {
    scrollLeft: i.scrollX,
    scrollTop: i.scrollY
  };
}
function B(i) {
  if (st(i) === "html")
    return i;
  const t = (
    // Step into the shadow DOM of the parent of a slotted node.
    i.assignedSlot || // DOM Element detected.
    i.parentNode || // ShadowRoot detected.
    fe(i) && i.host || // Fallback.
    R(i)
  );
  return fe(t) ? t.host : t;
}
function Se(i) {
  const t = B(i);
  return ot(t) ? i.ownerDocument ? i.ownerDocument.body : i.body : k(t) && bt(t) ? t : Se(t);
}
function gt(i, t, e) {
  var n;
  t === void 0 && (t = []), e === void 0 && (e = !0);
  const o = Se(i), s = o === ((n = i.ownerDocument) == null ? void 0 : n.body), r = _(o);
  if (s) {
    const a = Bt(r);
    return t.concat(r, r.visualViewport || [], bt(o) ? o : [], a && e ? gt(a) : []);
  }
  return t.concat(o, gt(o, [], e));
}
function Bt(i) {
  return i.parent && Object.getPrototypeOf(i.parent) ? i.frameElement : null;
}
function Ce(i) {
  const t = P(i);
  let e = parseFloat(t.width) || 0, n = parseFloat(t.height) || 0;
  const o = k(i), s = o ? i.offsetWidth : e, r = o ? i.offsetHeight : n, a = Ct(e) !== s || Ct(n) !== r;
  return a && (e = s, n = r), {
    width: e,
    height: n,
    $: a
  };
}
function Kt(i) {
  return O(i) ? i : i.contextElement;
}
function tt(i) {
  const t = Kt(i);
  if (!k(t))
    return T(1);
  const e = t.getBoundingClientRect(), {
    width: n,
    height: o,
    $: s
  } = Ce(t);
  let r = (s ? Ct(e.width) : e.width) / n, a = (s ? Ct(e.height) : e.height) / o;
  return (!r || !Number.isFinite(r)) && (r = 1), (!a || !Number.isFinite(a)) && (a = 1), {
    x: r,
    y: a
  };
}
const Ni = /* @__PURE__ */ T(0);
function Oe(i) {
  const t = _(i);
  return !Xt() || !t.visualViewport ? Ni : {
    x: t.visualViewport.offsetLeft,
    y: t.visualViewport.offsetTop
  };
}
function Ii(i, t, e) {
  return t === void 0 && (t = !1), !e || t && e !== _(i) ? !1 : t;
}
function J(i, t, e, n) {
  t === void 0 && (t = !1), e === void 0 && (e = !1);
  const o = i.getBoundingClientRect(), s = Kt(i);
  let r = T(1);
  t && (n ? O(n) && (r = tt(n)) : r = tt(i));
  const a = Ii(s, e, n) ? Oe(s) : T(0);
  let l = (o.left + a.x) / r.x, c = (o.top + a.y) / r.y, f = o.width / r.x, h = o.height / r.y;
  if (s) {
    const p = _(s), d = n && O(n) ? _(n) : n;
    let u = p, m = Bt(u);
    for (; m && n && d !== u; ) {
      const g = tt(m), w = m.getBoundingClientRect(), y = P(m), v = w.left + (m.clientLeft + parseFloat(y.paddingLeft)) * g.x, $ = w.top + (m.clientTop + parseFloat(y.paddingTop)) * g.y;
      l *= g.x, c *= g.y, f *= g.x, h *= g.y, l += v, c += $, u = _(m), m = Bt(u);
    }
  }
  return Pt({
    width: f,
    height: h,
    x: l,
    y: c
  });
}
function Gt(i, t) {
  const e = Lt(i).scrollLeft;
  return t ? t.left + e : J(R(i)).left + e;
}
function Pe(i, t, e) {
  e === void 0 && (e = !1);
  const n = i.getBoundingClientRect(), o = n.left + t.scrollLeft - (e ? 0 : (
    // RTL <body> scrollbar.
    Gt(i, n)
  )), s = n.top + t.scrollTop;
  return {
    x: o,
    y: s
  };
}
function zi(i) {
  let {
    elements: t,
    rect: e,
    offsetParent: n,
    strategy: o
  } = i;
  const s = o === "fixed", r = R(n), a = t ? Rt(t.floating) : !1;
  if (n === r || a && s)
    return e;
  let l = {
    scrollLeft: 0,
    scrollTop: 0
  }, c = T(1);
  const f = T(0), h = k(n);
  if ((h || !h && !s) && ((st(n) !== "body" || bt(r)) && (l = Lt(n)), k(n))) {
    const d = J(n);
    c = tt(n), f.x = d.x + n.clientLeft, f.y = d.y + n.clientTop;
  }
  const p = r && !h && !s ? Pe(r, l, !0) : T(0);
  return {
    width: e.width * c.x,
    height: e.height * c.y,
    x: e.x * c.x - l.scrollLeft * c.x + f.x + p.x,
    y: e.y * c.y - l.scrollTop * c.y + f.y + p.y
  };
}
function Bi(i) {
  return Array.from(i.getClientRects());
}
function ji(i) {
  const t = R(i), e = Lt(i), n = i.ownerDocument.body, o = X(t.scrollWidth, t.clientWidth, n.scrollWidth, n.clientWidth), s = X(t.scrollHeight, t.clientHeight, n.scrollHeight, n.clientHeight);
  let r = -e.scrollLeft + Gt(i);
  const a = -e.scrollTop;
  return P(n).direction === "rtl" && (r += X(t.clientWidth, n.clientWidth) - o), {
    width: o,
    height: s,
    x: r,
    y: a
  };
}
function Fi(i, t) {
  const e = _(i), n = R(i), o = e.visualViewport;
  let s = n.clientWidth, r = n.clientHeight, a = 0, l = 0;
  if (o) {
    s = o.width, r = o.height;
    const c = Xt();
    (!c || c && t === "fixed") && (a = o.offsetLeft, l = o.offsetTop);
  }
  return {
    width: s,
    height: r,
    x: a,
    y: l
  };
}
const Vi = /* @__PURE__ */ new Set(["absolute", "fixed"]);
function Wi(i, t) {
  const e = J(i, !0, t === "fixed"), n = e.top + i.clientTop, o = e.left + i.clientLeft, s = k(i) ? tt(i) : T(1), r = i.clientWidth * s.x, a = i.clientHeight * s.y, l = o * s.x, c = n * s.y;
  return {
    width: r,
    height: a,
    x: l,
    y: c
  };
}
function pe(i, t, e) {
  let n;
  if (t === "viewport")
    n = Fi(i, e);
  else if (t === "document")
    n = ji(R(i));
  else if (O(t))
    n = Wi(t, e);
  else {
    const o = Oe(i);
    n = {
      x: t.x - o.x,
      y: t.y - o.y,
      width: t.width,
      height: t.height
    };
  }
  return Pt(n);
}
function Te(i, t) {
  const e = B(i);
  return e === t || !O(e) || ot(e) ? !1 : P(e).position === "fixed" || Te(e, t);
}
function qi(i, t) {
  const e = t.get(i);
  if (e)
    return e;
  let n = gt(i, [], !1).filter((a) => O(a) && st(a) !== "body"), o = null;
  const s = P(i).position === "fixed";
  let r = s ? B(i) : i;
  for (; O(r) && !ot(r); ) {
    const a = P(r), l = Yt(r);
    !l && a.position === "fixed" && (o = null), (s ? !l && !o : !l && a.position === "static" && !!o && Vi.has(o.position) || bt(r) && !l && Te(i, r)) ? n = n.filter((f) => f !== r) : o = a, r = B(r);
  }
  return t.set(i, n), n;
}
function Yi(i) {
  let {
    element: t,
    boundary: e,
    rootBoundary: n,
    strategy: o
  } = i;
  const r = [...e === "clippingAncestors" ? Rt(t) ? [] : qi(t, this._c) : [].concat(e), n], a = r[0], l = r.reduce((c, f) => {
    const h = pe(t, f, o);
    return c.top = X(h.top, c.top), c.right = nt(h.right, c.right), c.bottom = nt(h.bottom, c.bottom), c.left = X(h.left, c.left), c;
  }, pe(t, a, o));
  return {
    width: l.right - l.left,
    height: l.bottom - l.top,
    x: l.left,
    y: l.top
  };
}
function Xi(i) {
  const {
    width: t,
    height: e
  } = Ce(i);
  return {
    width: t,
    height: e
  };
}
function Ki(i, t, e) {
  const n = k(t), o = R(t), s = e === "fixed", r = J(i, !0, s, t);
  let a = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const l = T(0);
  function c() {
    l.x = Gt(o);
  }
  if (n || !n && !s)
    if ((st(t) !== "body" || bt(o)) && (a = Lt(t)), n) {
      const d = J(t, !0, s, t);
      l.x = d.x + t.clientLeft, l.y = d.y + t.clientTop;
    } else o && c();
  s && !n && o && c();
  const f = o && !n && !s ? Pe(o, a) : T(0), h = r.left + a.scrollLeft - l.x - f.x, p = r.top + a.scrollTop - l.y - f.y;
  return {
    x: h,
    y: p,
    width: r.width,
    height: r.height
  };
}
function Nt(i) {
  return P(i).position === "static";
}
function ue(i, t) {
  if (!k(i) || P(i).position === "fixed")
    return null;
  if (t)
    return t(i);
  let e = i.offsetParent;
  return R(i) === e && (e = e.ownerDocument.body), e;
}
function ke(i, t) {
  const e = _(i);
  if (Rt(i))
    return e;
  if (!k(i)) {
    let o = B(i);
    for (; o && !ot(o); ) {
      if (O(o) && !Nt(o))
        return o;
      o = B(o);
    }
    return e;
  }
  let n = ue(i, t);
  for (; n && ki(n) && Nt(n); )
    n = ue(n, t);
  return n && ot(n) && Nt(n) && !Yt(n) ? e : n || Ui(i) || e;
}
const Gi = async function(i) {
  const t = this.getOffsetParent || ke, e = this.getDimensions, n = await e(i.floating);
  return {
    reference: Ki(i.reference, await t(i.floating), i.strategy),
    floating: {
      x: 0,
      y: 0,
      width: n.width,
      height: n.height
    }
  };
};
function Ji(i) {
  return P(i).direction === "rtl";
}
const Zi = {
  convertOffsetParentRelativeRectToViewportRelativeRect: zi,
  getDocumentElement: R,
  getClippingRect: Yi,
  getOffsetParent: ke,
  getElementRects: Gi,
  getClientRects: Bi,
  getDimensions: Xi,
  getScale: tt,
  isElement: O,
  isRTL: Ji
};
function Re(i, t) {
  return i.x === t.x && i.y === t.y && i.width === t.width && i.height === t.height;
}
function Qi(i, t) {
  let e = null, n;
  const o = R(i);
  function s() {
    var a;
    clearTimeout(n), (a = e) == null || a.disconnect(), e = null;
  }
  function r(a, l) {
    a === void 0 && (a = !1), l === void 0 && (l = 1), s();
    const c = i.getBoundingClientRect(), {
      left: f,
      top: h,
      width: p,
      height: d
    } = c;
    if (a || t(), !p || !d)
      return;
    const u = At(h), m = At(o.clientWidth - (f + p)), g = At(o.clientHeight - (h + d)), w = At(f), v = {
      rootMargin: -u + "px " + -m + "px " + -g + "px " + -w + "px",
      threshold: X(0, nt(1, l)) || 1
    };
    let $ = !0;
    function x(L) {
      const E = L[0].intersectionRatio;
      if (E !== l) {
        if (!$)
          return r();
        E ? r(!1, E) : n = setTimeout(() => {
          r(!1, 1e-7);
        }, 1e3);
      }
      E === 1 && !Re(c, i.getBoundingClientRect()) && r(), $ = !1;
    }
    try {
      e = new IntersectionObserver(x, {
        ...v,
        // Handle <iframe>s
        root: o.ownerDocument
      });
    } catch {
      e = new IntersectionObserver(x, v);
    }
    e.observe(i);
  }
  return r(!0), s;
}
function tn(i, t, e, n) {
  n === void 0 && (n = {});
  const {
    ancestorScroll: o = !0,
    ancestorResize: s = !0,
    elementResize: r = typeof ResizeObserver == "function",
    layoutShift: a = typeof IntersectionObserver == "function",
    animationFrame: l = !1
  } = n, c = Kt(i), f = o || s ? [...c ? gt(c) : [], ...gt(t)] : [];
  f.forEach((w) => {
    o && w.addEventListener("scroll", e, {
      passive: !0
    }), s && w.addEventListener("resize", e);
  });
  const h = c && a ? Qi(c, e) : null;
  let p = -1, d = null;
  r && (d = new ResizeObserver((w) => {
    let [y] = w;
    y && y.target === c && d && (d.unobserve(t), cancelAnimationFrame(p), p = requestAnimationFrame(() => {
      var v;
      (v = d) == null || v.observe(t);
    })), e();
  }), c && !l && d.observe(c), d.observe(t));
  let u, m = l ? J(i) : null;
  l && g();
  function g() {
    const w = J(i);
    m && !Re(m, w) && e(), m = w, u = requestAnimationFrame(g);
  }
  return e(), () => {
    var w;
    f.forEach((y) => {
      o && y.removeEventListener("scroll", e), s && y.removeEventListener("resize", e);
    }), h == null || h(), (w = d) == null || w.disconnect(), d = null, l && cancelAnimationFrame(u);
  };
}
const en = Ci, nn = Oi, on = _i, rn = Ai, sn = (i, t, e) => {
  const n = /* @__PURE__ */ new Map(), o = {
    platform: Zi,
    ...e
  }, s = {
    ...o.platform,
    _c: n
  };
  return $i(i, t, {
    ...o,
    platform: s
  });
}, an = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
function ln(i, t, e, n) {
  if (!i) return;
  const o = (e == null ? void 0 : e.offsetWidth) ?? 0, s = hn(n);
  return tn(
    i,
    t,
    () => {
      sn(i, t, {
        placement: s,
        middleware: [
          nn(),
          on(),
          en(16),
          rn({ element: e })
        ]
      }).then(({ x: r, y: a, middlewareData: l, placement: c }) => {
        Object.assign(t.style, {
          left: `${r}px`,
          top: `${a}px`
        });
        const f = c.split("-")[0], h = an[f];
        if (!h) throw new Error("Invalid side");
        if (l.arrow) {
          const { x: p, y: d } = l.arrow;
          Object.assign(e.style, {
            // Use != to check for null or undefined and allow 0 values
            left: p != null ? `${p}px` : "",
            top: d != null ? `${d}px` : "",
            // Ensure the opposite side gets unset when
            // flipping to other placements' axes.
            right: "",
            bottom: "",
            // Add two pixels for smoother arrow placement
            [h]: `${(-o + 2) / 2}px`,
            transform: "rotate(45deg)"
          });
        }
      });
    },
    { animationFrame: !0 }
  );
}
const cn = {
  "above-start": "top-start",
  above: "top",
  "above-end": "top-end",
  "below-start": "bottom-start",
  below: "bottom",
  "below-end": "bottom-end",
  "before-top": "bottom-start",
  before: "left",
  "before-bottom": "left-end",
  "after-top": "right-start",
  after: "right",
  "after-bottom": "right-end"
}, hn = (i) => {
  const t = cn[i];
  if (!t)
    throw new Error(`Unsupported placement: ${i}`);
  return t;
};
var dn = Object.defineProperty, Z = (i, t, e, n) => {
  for (var o = void 0, s = i.length - 1, r; s >= 0; s--)
    (r = i[s]) && (o = r(t, e, o) || o);
  return o && dn(t, e, o), o;
};
const Jt = class Jt extends pt {
  constructor() {
    super(), this.elementInternals = this.attachInternals(), this.defaultDelay = 300, this.position = "above", this.id = di("a11y-bdk-tooltip"), this.mouseenterAnchorHandler = () => this.showTooltip(this.delay), this.clickAnchorHandler = () => this.showTooltip(this.delay), this.mouseleaveAnchorHandler = () => this.hideTooltip(this.delay), this.focusAnchorHandler = () => this.showTooltip(0), this.blurAnchorHandler = () => this.hideTooltip(0), this.elementInternals.role = "tooltip";
  }
  get anchorElement() {
    var e;
    if (((e = this.previousElementSibling) == null ? void 0 : e.getAttribute("id")) === this.anchorId)
      return this.previousElementSibling;
    const t = this.getRootNode();
    return (t instanceof ShadowRoot ? t : document).getElementById(this.anchorId ?? "");
  }
  connectedCallback() {
    if (super.connectedCallback(), !this.anchorElement)
      throw new Error("no anchor element found with id: " + this.anchorId);
    this.popover = "", this.setAttribute("aria-hidden", "true"), this.setAttribute("role", "tooltip");
    const t = this.anchorElement.getAttribute("aria-describedby");
    this.anchorElement.setAttribute(
      "aria-describedby",
      t ? `${t} ${this.id}` : this.id
    ), this.setAttribute("id", this.id), this.anchorElement.addEventListener("focus", this.focusAnchorHandler), this.anchorElement.addEventListener("blur", this.blurAnchorHandler), this.anchorElement.addEventListener(
      "mouseenter",
      this.mouseenterAnchorHandler
    ), this.anchorElement.addEventListener(
      "mouseleave",
      this.mouseleaveAnchorHandler
    ), this.anchorElement.addEventListener("click", this.clickAnchorHandler);
  }
  disconnectedCallback() {
    var e, n, o, s, r, a, l, c, f, h;
    super.disconnectedCallback(), clearTimeout(this.showTimeoutId), clearTimeout(this.hideTimeoutId), (e = this.cleanupAutoUpdate) == null || e.call(this), this.cleanupAutoUpdate = void 0, (n = this.anchorElement) == null || n.removeEventListener("focus", this.focusAnchorHandler), (o = this.anchorElement) == null || o.removeEventListener("blur", this.blurAnchorHandler), (s = this.anchorElement) == null || s.removeEventListener(
      "mouseenter",
      this.mouseenterAnchorHandler
    ), (r = this.anchorElement) == null || r.removeEventListener(
      "mouseleave",
      this.mouseleaveAnchorHandler
    ), (a = this.anchorElement) == null || a.removeEventListener("click", this.clickAnchorHandler);
    const t = (c = (l = this.anchorElement) == null ? void 0 : l.getAttribute("aria-describedby")) == null ? void 0 : c.replace(this.id, "").trim();
    t ? (h = this.anchorElement) == null || h.setAttribute("aria-describedby", t) : (f = this.anchorElement) == null || f.removeAttribute("aria-describedby");
  }
  showTooltip(t = this.defaultDelay) {
    var e;
    (e = this.anchorElement) != null && e.hasAttribute("disabled") || (clearTimeout(this.hideTimeoutId), this.showTimeoutId = setTimeout(() => {
      this.setAttribute("aria-hidden", "false"), this.showPopover(), this.cleanupAutoUpdate = ln(
        this.anchorElement,
        this,
        this.tooltipArrow,
        this.position
      );
    }, t));
  }
  hideTooltip(t = this.defaultDelay) {
    var e;
    this.matches(":hover") || (e = this.anchorElement) != null && e.matches(":hover") || document.activeElement === this.anchorElement || (clearTimeout(this.showTimeoutId), this.hideTimeoutId = setTimeout(() => {
      var n;
      (n = this.cleanupAutoUpdate) == null || n.call(this), this.cleanupAutoUpdate = void 0, this.setAttribute("aria-hidden", "true"), this.hidePopover();
    }, t));
  }
  render() {
    return $t`
      <div aria-hidden="true" class="tooltip--arrow"></div>
      <div class="tooltip--content">
        ${this.iconHTML()} ${this.headerHTML()} ${this.contentHTML()}
      </div>
    `;
  }
  iconHTML() {
    return this.hasSlotContent("icon") ? $t`<div class="tooltip--icon">
      <slot name="icon"></slot>
    </div>` : null;
  }
  headerHTML() {
    if (this.headline)
      return $t`<div class="tooltip--header">${this.headline}</div>`;
  }
  contentHTML() {
    if (this.content)
      return $t`<div class="tooltip--text">${this.content}</div>`;
  }
  hasSlotContent(t) {
    return !!this.querySelector(`[slot="${t}"]`);
  }
};
Jt.styles = ge(ai);
let U = Jt;
Z([
  rt({ type: String, attribute: "anchor-id" })
], U.prototype, "anchorId");
Z([
  rt({ type: String })
], U.prototype, "content");
Z([
  rt({ type: String, reflect: !0 })
], U.prototype, "position");
Z([
  rt({ type: String })
], U.prototype, "headline");
Z([
  rt({ type: Number })
], U.prototype, "delay");
Z([
  rt({ type: String, reflect: !0 })
], U.prototype, "id");
Z([
  si(".tooltip--arrow")
], U.prototype, "tooltipArrow");
export {
  U as BdkTooltip
};
